import {
  NgModule,
  APP_INITIALIZER,
  ErrorHandler,
  Injectable
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  ArcSharedModule,
  clearStateMetaReducer,
  ApiRequestRetryInterceptor,
  SessionTimeOutInterceptor,
  TokenInterceptor,
  SessionTTLInterceptor,
  GoogleTagManagerModule,
  CustomMfeTranslateLoader,
  BrowserStorageService
} from '@arc/arc-shared-frontend';
import { StoreModule, ActionReducerMap, MetaReducer } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader } from '@ngx-translate/core';
import { AppRoutes } from './app.routes';
import { AppComponent } from './app.component';
import { BreadcrumbService } from './breadcrumb.service';
import {
  AppHomeComponent,
  AppMenuComponent,
  AppSubMenuComponent,
  AppFooterComponent,
  AppInlineProfileComponent,
  AppRightpanelComponent,
  AppTopbarComponent
} from './shared';
import { TranslateModule } from '@ngx-translate/core';
import { CardMerchantRegistrationModule } from '@arc/card-merchant-registration';
import { ArcConfigurationModule } from '@arc/arc-configuration-frontend';
import { TranslateService } from '@ngx-translate/core';
import { SSOResultComponent } from './ssoresult/ssoresult.component';
import {BrowserCookieService} from '@arc/arc-shared-frontend';
/* Import prism core */
import 'prismjs/prism';
/* Import the language you need to highlight */
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-scss';
import 'prismjs/components/prism-typescript';
import { Error401Component } from './errorpages/error401.component';
import { DragulaModule } from 'ng2-dragula';
import { environment } from '../environments/environment';
import { WorkspaceContainerComponent } from './workspace/workspace.container.component';
import { AppBreadcrumbComponent } from './app.breadcrumb.component';
import { ArcUserModule } from '@arc/arc-user-frontend';
import { ArcAccountModule } from '@arc/account-frontend';
import { ConfigurationLazyModule } from './lazymodulewrappers/configuration.lazy.module';
import {
  cashappHttpInterceptors,
  UserPermissionsModule as CashAppUserPermissionsModule
} from '@arc/arc-cashapp-frontend';
import { ClipboardModule } from 'ngx-clipboard';
import { ArcCreditappFrontendModule } from '@arc/arc-creditapp-frontend';
import { CstAppComponent } from './workspace/apps/cstapp/cstapp.component';
import { ArcCreditCommonModule } from '@arc/arc-credit-common';
import { WorkplaceRootComponent } from './workspace/workplace-root.component';
import { AureusModule } from '@arc/aureus';
import { TINYMCE_SCRIPT_SRC, EditorModule as TinyMceEditorModule } from '@tinymce/tinymce-angular';
import { DropdownModule } from 'primeng/dropdown';
import { TabMenuModule } from 'primeng/tabmenu';
import { ClientConnectComponent } from './workspace/apps/clientconnect/clientconnect.component';
import { NavigationModule } from './navigation/navigation.module';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { AuthenticationModule } from './authentication/authentication.module';
import { AppNameService } from '@arc/arc-shared-frontend';

export const reducers: ActionReducerMap<any> = {};

export const metaReducers: MetaReducer<any>[] = [clearStateMetaReducer];

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/languages/', '.json');
}
export function ResellerBasedValueFactory(appNameService: AppNameService) {
  return appNameService.name$;
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  handleError = console.error;
}

@NgModule({ declarations: [
        AppComponent,
        AppHomeComponent,
        AppMenuComponent,
        AppFooterComponent,
        AppSubMenuComponent,
        AppInlineProfileComponent,
        AppTopbarComponent,
        AppRightpanelComponent,
        AppBreadcrumbComponent,
        SSOResultComponent,
        Error401Component,
        WorkspaceContainerComponent,
        WorkplaceRootComponent,
        CstAppComponent,
        ClientConnectComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        //preserve the functionality of the [disabled] attribute
        FormsModule.withConfig({
            callSetDisabledState: 'whenDisabledForLegacyCode'
        }),
        ReactiveFormsModule.withConfig({
            callSetDisabledState: 'whenDisabledForLegacyCode'
        }),
        AureusModule,
        TinyMceEditorModule,
        CardMerchantRegistrationModule,
        DropdownModule,
        TabMenuModule,
        AppRoutes,
        BrowserAnimationsModule,
        ClipboardModule,
        OverlayPanelModule,
        StoreModule.forRoot(reducers, {
            metaReducers,
            runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: false,
                strictStateSerializability: false
            }
        }),
        EffectsModule.forRoot([]),
        ArcSharedModule,
        ArcUserModule,
        ArcAccountModule,
        ArcConfigurationModule,
        ConfigurationLazyModule,
        ArcCreditappFrontendModule,
        ArcCreditCommonModule,
        AuthenticationModule,
        NavigationModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: CustomMfeTranslateLoader,
                deps: [HttpClient]
            }
        }),
        StoreDevtoolsModule.instrument({
            maxAge: 25,
            connectInZone: true
        }),
        GoogleTagManagerModule.forRoot({
            id: 'GTM-WDBN5LT'
        }),
        DragulaModule.forRoot(),
        CashAppUserPermissionsModule], providers: [
        {
            provide: 'appName',
            useFactory: ResellerBasedValueFactory,
            deps: [AppNameService],
        },
        {
            provide: APP_INITIALIZER,
            deps: [TranslateService, BrowserStorageService],
            useFactory: (translateService: TranslateService, storageService: BrowserStorageService) => {
                return () => new Promise<any>((resolve: any) => {
                    const userDefaultLang = storageService.getItem('arc-userDefaultLang');
                    // Set default lang to 'en'. Some tokens are only translated in en.json files
                    translateService.setDefaultLang('en');
                    if (userDefaultLang && userDefaultLang !== '') {
                        translateService.use(userDefaultLang).subscribe({
                            next: (v) => console.info(`Successfully initialized '${userDefaultLang}' language.`),
                            error: (e) => console.error(`Problem with '${userDefaultLang}' language initialization.`),
                            complete: () => resolve(null)
                        });
                    }
                    else {
                        translateService.use('en').subscribe({
                            next: (v) => console.info(`Successfully initialized 'en' language default.`),
                            error: (e) => console.error(`Problem with 'en' language initialization default.`),
                            complete: () => resolve(null)
                        });
                    }
                });
            },
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ApiRequestRetryInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SessionTimeOutInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SessionTTLInterceptor,
            multi: true
        },
        { 
            // Force loading js from self-hosted location
            provide: TINYMCE_SCRIPT_SRC, 
            useValue: 'assets/lib/tinymce/tinymce.min.js' 
        }, 
        cashappHttpInterceptors,
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        BreadcrumbService,
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {}
